import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Crown, Phone } from 'lucide-react';
import Button from '../ui/Button';
import AnnouncementService from '../../services/AnnouncementService';

// The incident's on-scene team leader, picked from its own roster alongside teams and
// hospitals. The server refuses activation without one, and refuses anyone not on the
// roster or who has rejected the assignment — offered choices mirror that.
export default function TeamLeaderPicker({ announcementId, teamLeader, members, canManage, locked, onChanged }) {
  const [selectedId, setSelectedId] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSelectedId(teamLeader?.id ? String(teamLeader.id) : '');
  }, [teamLeader?.id]);

  const candidates = (members || [])
    .filter((m) => m.member && m.accepted !== false)
    .sort((a, b) => a.member.fullname.localeCompare(b.member.fullname));

  const handleSave = async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      await AnnouncementService.setTeamLeader(announcementId, Number(selectedId));
      toast.success('Team leader assigned.');
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to assign team leader.');
    } finally {
      setSaving(false);
    }
  };

  const unchanged = String(teamLeader?.id || '') === selectedId;

  return (
    <div className={`mb-5 rounded-xl border p-4 flex flex-col lg:flex-row lg:items-center gap-4 ${
      teamLeader
        ? 'border-seha-100 bg-seha-50 dark:border-seha-500/20 dark:bg-seha-500/10'
        : 'border-orange-200 bg-orange-50 dark:border-orange-500/20 dark:bg-orange-500/10'
    }`}>
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
          teamLeader ? 'bg-seha-500 text-white' : 'bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-400'
        }`}>
          <Crown className="w-5 h-5" />
        </div>
        <div className="min-w-0">
          <div className="text-[10px] font-semibold uppercase tracking-widest text-neutral-500 dark:text-neutral-400">Team Leader</div>
          {teamLeader ? (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
              <span className="font-bold text-neutral-800 dark:text-neutral-100 truncate">{teamLeader.fullname}</span>
              {teamLeader.mobile && (
                <span className="inline-flex items-center gap-1 text-xs text-neutral-500 dark:text-neutral-400">
                  <Phone className="w-3 h-3" />
                  {teamLeader.mobile}
                </span>
              )}
            </div>
          ) : (
            <div className="text-sm font-semibold text-orange-700 dark:text-orange-400">
              Not assigned — required before activation
            </div>
          )}
        </div>
      </div>

      {canManage && !locked && (
        <div className="flex flex-col sm:flex-row gap-2 lg:w-[420px]">
          <select
            className="form-input bg-white dark:bg-ink-900 flex-1"
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            disabled={candidates.length === 0 || saving}
            aria-label="Choose team leader"
          >
            <option value="">{candidates.length === 0 ? 'Assign a team or responder first' : 'Choose from the roster…'}</option>
            {candidates.map((m) => (
              <option key={m.id} value={m.member.id}>
                {m.member.fullname}{m.team?.name ? ` — ${m.team.name}` : ''}
              </option>
            ))}
          </select>
          <Button variant="primary" size="md" onClick={handleSave} disabled={!selectedId || unchanged || saving}>
            {teamLeader ? 'Change' : 'Assign'}
          </Button>
        </div>
      )}
    </div>
  );
}
